import { TypePieceResolu as TypePieceResoluEntity } from '../service/type-piece-resolution';
import { FormApiControleQuinquennal } from 'form-api';
import { TicketControleQuinquennalStatus } from '../../ticket/controller/ticket-controle-quinquennal.definition';

type ControleQuinquennalPipelineStage = {
  pipelineStage: TicketControleQuinquennalStatus;
};

export type ControleQuinquennal =
  FormApiControleQuinquennal.ControleQuinquennal;
export type ControleQuinquennalWithCompletion =
  FormApiControleQuinquennal.ControleQuinquennalWithCompletion &
    ControleQuinquennalPipelineStage;
export type ControleQuinquennalWithAxes =
  FormApiControleQuinquennal.ControleQuinquennalWithAxes &
    ControleQuinquennalPipelineStage;
/**
 * Fiche d'un contrôle. Sans métriques : leur calcul passe par HubSpot puis par
 * tous les questionnaires du contrôle, ce qui retarderait l'ouverture du
 * dossier. Le front les demande à part (`POST metriques`).
 */
export type ControleQuinquennalAdminResponse =
  FormApiControleQuinquennal.ControleQuinquennalWithAxes &
    ControleQuinquennalPipelineStage;

/**
 * Liste admin : n'expose pas les axes, seulement leurs compteurs — la requête
 * les charge mais le mapping ne les recopie pas.
 */
/**
 * `estMonControle` : le contrôle appartient à l'utilisateur connecté (owner
 * HubSpot de la session), que le front ne peut pas lire lui-même.
 */
export type ControleQuinquennalAdminListResponse =
  FormApiControleQuinquennal.ControleQuinquennalAdminListItem &
    ControleQuinquennalPipelineStage & { estMonControle: boolean };

export type ControleQuinquennalMetriquesAvecSiren =
  FormApiControleQuinquennal.ControleQuinquennalMetriquesAvecSiren;

export type ControleQuinquennalExportEnrichment = {
  siren: string | null;
  phaseCycleDeVie: string | null;
  entrepriseParent: string | null;
  numeroOrias: string | null;
  activitesOrias: string | null;
  activitesAccessoires: string | null;
  chiffreAffaire: number | null;
  nombrePersonnesAssujetties: number | null;
  mandants: string | null;
  contactAdmin: string | null;
  verificateurProprietaireTicketCq: string | null;
};

export type ControleQuinquennalAdminExportResponse =
  FormApiControleQuinquennal.ControleQuinquennalWithAxes &
    ControleQuinquennalPipelineStage &
    ControleQuinquennalExportEnrichment;
export type OriasStatut = FormApiControleQuinquennal.OriasStatut;
export const OriasStatut = FormApiControleQuinquennal.OriasStatut;
export type ControleQuinquennalAxeType =
  FormApiControleQuinquennal.ControleQuinquennalAxeType;
export const ControleQuinquennalAxeType =
  FormApiControleQuinquennal.ControleQuinquennalAxeType;
export type ControleQuinquennalStatus =
  FormApiControleQuinquennal.ControleQuinquennalStatus;
export const ControleQuinquennalStatus =
  FormApiControleQuinquennal.ControleQuinquennalStatus;
export type ControleQuinquennalAxeStatus =
  FormApiControleQuinquennal.ControleQuinquennalAxeStatus;
export const ControleQuinquennalAxeStatus =
  FormApiControleQuinquennal.ControleQuinquennalAxeStatus;
export type VerificationStatus = FormApiControleQuinquennal.VerificationStatus;
export const VerificationStatus = FormApiControleQuinquennal.VerificationStatus;
export type ControleQuinquennalAxeUpdate =
  FormApiControleQuinquennal.ControleQuinquennalAxeUpdate;
export type ControleQuinquennalAxeWithNumberOfFiles =
  FormApiControleQuinquennal.ControleQuinquennalAxeWithNumberOfFiles;

export type ControleQuinquennalAxe =
  FormApiControleQuinquennal.ControleQuinquennalAxe;
export type ControleQuinquennalAxeWithFiles =
  FormApiControleQuinquennal.ControleQuinquennalAxeWithFiles;
export type ControleQuinquennalAxeWithFilesAndTotal = {
  axe: ControleQuinquennalAxeWithFiles;
  total: number;
};
export type ControleQuinquennalAxeFile =
  FormApiControleQuinquennal.ControleQuinquennalAxeFile;

export type {
  TypePieceResolu,
  TypePieceSource,
} from '../service/type-piece-resolution';

/**
 * Pièce telle que les fronts la reçoivent : le type à afficher est résolu côté
 * gateway, les fronts n'ont pas à rejouer la cascade déclaré / détecté / défaut.
 */
export type ControleQuinquennalAxeFileAvecType =
  FormApiControleQuinquennal.ControleQuinquennalAxeFile & {
    typePiece: TypePieceResoluEntity;
  };

export type ControleQuinquennalAxeWithFilesAvecTypes = Omit<
  FormApiControleQuinquennal.ControleQuinquennalAxeWithFiles,
  'controleQuinquennalAxeFiles'
> & {
  controleQuinquennalAxeFiles: ControleQuinquennalAxeFileAvecType[];
};

export type ControleQuinquennalAxeWithFilesAvecTypesAndTotal = {
  axe: ControleQuinquennalAxeWithFilesAvecTypes;
  total: number;
};

export type ControleQuinquennalAiResult =
  FormApiControleQuinquennal.ControleQuinquennalAiResult;
export type ControleQuinquennalAiAnswer =
  FormApiControleQuinquennal.ControleQuinquennalAiAnswer;
export type ControleQuinquennalDimension =
  FormApiControleQuinquennal.ControleQuinquennalDimension;
export type ControleQuinquennalSujet =
  FormApiControleQuinquennal.ControleQuinquennalSujet;
export type ControleQuinquennalVerdictSousAxe =
  FormApiControleQuinquennal.ControleQuinquennalVerdictSousAxe;
export type ControleQuinquennalUnreadDocument = NonNullable<
  ControleQuinquennalAiResult['unreadDocuments']
>[number];
export type ControleQuinquennalUnreadReason =
  ControleQuinquennalUnreadDocument['reason'];

/**
 * Ligne de vérification PAR SOUS-AXE (route `.../verifications`), vide tant que
 * `CQ_ASSUJETTI_AI !== 'on'`. Projection sans `preVerdict` ni lignes `''` (axes
 * société). `resultPayload.verdict` peut manquer (LISTE) et `resultPayload` est
 * null pour les lignes FAILED/PENDING.
 */
export type ControleQuinquennalSubAxisVerification = {
  subAxis: string;
  assujettiId: string | null;
  dimension: string | null;
  status: VerificationStatus;
  startedAt: Date | string;
  endedAt: Date | string | null;
  errorMessage: string | null;
  resultPayload: ControleQuinquennalAiResult | null;
};

/** MR-G — réponse de la ré-émission unitaire d'un sous-axe. */
export type RelaunchSubAxisAnalysisResponse = {
  /** `processing` : message ré-émis ; `resolved` : sous-axe re-résolu par règles. */
  status: 'processing' | 'resolved';
};

export type ControleQuinquenalRemediationRequestType =
  FormApiControleQuinquennal.ControleQuinquennalRemediationRequestType;
export type ControleQuinquenalRemediationResponseType =
  FormApiControleQuinquennal.ControleQuinquennalRemediationResponseType;
export type ControleQuinquennalRemediationWithFileResponseType =
  FormApiControleQuinquennal.ControleQuinquennalRemediationWithFileResponseType;
export type ControleQuinquennalRapport =
  FormApiControleQuinquennal.ControleQuinquennalRapport;
export type ControleQuinquennalSearchRequestType =
  FormApiControleQuinquennal.ControleQuinquennalSearchRequestType;
export type ControleQuinquennalAdminCompteurs =
  FormApiControleQuinquennal.ControleQuinquennalAdminCompteurs;

/**
 * Query de la liste admin : `mesControles` ne franchit pas la gateway, qui le
 * résout en `verificateurId` depuis la session avant d'appeler la form-api.
 */
export type ControleQuinquennalAdminSearchRequestType =
  ControleQuinquennalSearchRequestType & {
    mesControles?: boolean;
  };

export interface ForcerDepotAdminSystemParamType {
  entrepriseId: string;
  millesime: number;
}

export interface DeposerParTicketAdminSystemParamType {
  idTicket: string;
}

export type RemediationConversationNote = {
  id: string;
  content: string;
  createdAt: string;
  auteur?: string;
};

export type RemediationConversation = {
  remediation: ControleQuinquennalRemediationWithFileResponseType | null;
  notes: RemediationConversationNote[];
  auteur?: string;
};

export type RemediationConversationResponse = {
  conversations: RemediationConversation[];
};

/**
 * Réponse de `GET :id/analyse/estimation`
 * (conception EN-1476 §3.5). Déclarée ici plutôt
 * qu'importée depuis le service d'estimation : le controller ne remonte
 * jamais un type service (hiérarchie controller → service).
 */
export type ControleQuinquennalAnalyseEstimationResponse = {
  rowCount: number;
  documentCount: number;
  estimatedDurationSeconds: number | null;
  estimatedCostUsd: number | null;
  durationSampleSize: number;
  costSampleSize: number;
  costThresholdUsd: number;
};

export type ControleQuinquennalCancelBatchRequestType = {
  ids: string[];
};

export type ControleQuinquennalCancelBatchResponse = {
  annules: {
    id: string;
    entrepriseName: string;
    millesime: number;
  }[];
  echecs: FormApiControleQuinquennal.ControleQuinquennalCancelBatchFailure[];
};
